/**
 * How Portless assembles a parcel — the ONE definition of "Hybrid vs Cross Dock".
 *
 * WHY THIS IS SHARED. This rule decides three physical outcomes:
 *   1. which manifest sheet an order's lines land on (portless-export/mapper.ts),
 *   2. whether a 4x6 SKU barcode sticker prints at packing
 *      (hybrid-sticker/hybrid-sticker.handler.ts),
 *   3. what the tracker + production stations tell an operator.
 *
 * It lives in `packages/shared` (not the backend) precisely BECAUSE the third
 * consumer is the UI: the Order Queue row already receives `tags`, so letting the
 * frontend re-test them would recreate the drift described below on the other side
 * of the wire. Import this; never re-derive.
 * Those had drifted: the sticker kept a private copy matching only `hybrid`, with
 * a comment claiming it was "consistent with the export partition" — which stopped
 * being true the moment `portless-threadlab-split` was added to the manifest side.
 * A `Portless-Threadlab-Split` order went on the Hybrid sheet and got no sticker.
 * Keep every consumer pointed here rather than re-deriving the rule.
 */
/**
 * Order tags that mean "Portless assembles this from two sources and may hold
 * half of it itself" (domain rule, Finn 2026-07-30):
 *   - `Hybrid`                   — the original marker.
 *   - `Portless-Threadlab-Split` — split between Portless (holds stock) and
 *     Thread Lab (produces). Measured on prod: 6 of 22 live Portless-held line
 *     items sat on orders carrying this and NOT `Hybrid`, so omitting it dropped
 *     them from every sheet.
 *
 * DELIBERATELY ABSENT: `Threadlab-Split` — a split WITHIN Thread Lab, no Portless
 * stock involved. That is Cross Dock.
 */
const HYBRID_TAGS = ['hybrid', 'portless-threadlab-split'];
/**
 * MATCHING IS EXACT AND MUST STAY THAT WAY.
 *
 * `portless-threadlab-split` CONTAINS `threadlab-split` as a substring, so a
 * substring/`includes()` test would classify every Hybrid order as Cross Dock —
 * silently inverting the routing for the one case this exists to handle. There is
 * a test asserting the substring relationship directly so that refactor fails
 * loudly. Case-insensitive + trimmed: the live Shopify tags are capitalised and
 * operator-entered tags carry stray whitespace.
 */
export function isHybridFulfilment(tags) {
    return (tags ?? []).some((t) => typeof t === 'string' && HYBRID_TAGS.includes(t.trim().toLowerCase()));
}
/** {@link isHybridFulfilment} as the named type, for display + API payloads. */
export function classifyPortlessFulfilment(tags) {
    return isHybridFulfilment(tags) ? 'hybrid' : 'crossdock';
}
